<template>
  <div class="reminder-container">
    <ThemedIcon name="close" @click="close" class="modal-close" />
    <div class="close-mobile">
      <svg
        class="closer"
        @click="close"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="4"
        viewBox="0 0 24 4"
        fill="none"
      >
        <path
          d="M2 2H22"
          stroke="#CDCDCD"
          stroke-width="3"
          stroke-linecap="round"
        />
      </svg>
    </div>
    <!-- TITULO DEL MODAL -->
    <div class="modal-header">
      <div class="titleContainer">
        <img src="@/assets/images/muni/plus-clock.svg" />
        <h1 class="h1-heroMuni">Ver Recordatorios</h1>
      </div>
    </div>
    <div class="modal-body" v-if="!loading">
      <div class="fields-container">
        <div class="section">
          <ul class="classic-list">
            <li v-for="reminder in reminders" :key="reminder">
              <div class="reminder-item">
                <div class="reminder-prop">
                  <h3 class="h3-defaultMuni">
                    {{ formatDate(reminder.scheduled_at) }}
                  </h3>
                </div>
                <div class="reminder-prop">
                  <h3 class="h3-defaultMuni">
                    {{ formatTime(reminder.scheduled_at) }}
                  </h3>
                </div>
                <div class="reminder-prop">
                  <h3 class="h3-heroMuni">{{ reminder.description }}</h3>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <div class="modal-body" style="align-items: center" v-else>
      <LoadingComponent />
    </div>
    <div class="modal-footer"></div>
  </div>
</template>

<script setup>
import { ref, onBeforeMount } from "vue";
import LoadingComponent from "@/components/Municipal/Modals/Carga/LoadingComponent.vue";
import ReminderService from "@/services/office/reminderService";
import ThemedIcon from "@/components/Utils/ThemedIcon.vue";

const loading = ref(false);
const reminders = ref([]);

const emit = defineEmits(["close-modal", "open-modal"]);

function formatDate(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    // Ajusta por UTC si la fecha viene con 'Z'
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Meses son 0-indexados
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  } catch (error) {
    console.error("Error formateando fecha:", error);
    return "";
  }
}

function formatTime(dateString) {
  if (!dateString) return "";
  try {
    const date = new Date(dateString);
    // Obtiene hora y minutos en la zona horaria local
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  } catch (error) {
    console.error("Error formateando hora:", error);
    return "";
  }
}

const getReminders = async () => {
  loading.value = true;
  const res = await ReminderService.getReminders();
  reminders.value = res?.data ?? [];
  loading.value = false;
};

onBeforeMount(() => {
  getReminders();
});

const close = () => {
  emit("close-modal");
};
</script>

<style scoped>
@import "@/components/Utils/css/form-styles.css";
.reminder-container {
  display: flex;
  flex-direction: column;
  /* height: 100%; */
  overflow: hidden;
  position: relative;
  padding: 0;
  height: 47vh;
  width: 40vw;
}
.modal-header {
  padding: 4.96vh 3.65vw 3.29vh 3.65vw;
  /* gap: 3.29vh; */
  display: flex;
  flex-direction: column;
  width: 100%;
}
.modal-header img {
  width: 1.32vw;
}
.close-mobile {
  display: none;
  visibility: collapse;
}
.closer {
  width: 6.67vw;
}
.modal-body {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0rem 3.65vw 0rem 3.65vw;
  overflow-y: auto;
}

.fields-container {
  display: flex;
  flex-direction: column;
  gap: 4.11vh;
}

.section {
  display: flex;
  flex-direction: column;
  height: fit-content;
  gap: 0.82vh;
  width: 100%;
}
.modal-close {
  position: fixed;
  right: 1.464vw;
  top: 2.838vh;
  background: none;
  border: none;
  font-size: calc(18px + (100vw - 1366px) * 0.01);
  cursor: pointer;
}

.content {
  gap: 2.504vh;
  padding: 5.008vh 0 10.017vh 0;
}

.modal-footer {
  min-height: 4.96vh;
}

.classic-list {
  list-style-type: none;
  width: 100%;
  padding: unset;
  margin: unset;
  display: flex;
  flex-direction: column;
  gap: 1.63vh;
}

.reminder-item {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  gap: 1.1vw;
  border-bottom: 0.016rem solid var(--text-primary);
  padding: 1.16vh 0px;
}

.reminder-prop {
  display: flex;
  justify-content: center;
  min-width: 5.2vw;
  flex-grow: 0;
}

.reminder-prop:last-child {
  justify-content: flex-start;
  flex-grow: 1;
}

/* -------------------------------------------------------------------------- */
/* RESPONSIVE MOBILE STYLES                         */
/* -------------------------------------------------------------------------- */

/* Define el punto de corte (breakpoint) para móvil, por ejemplo, 768px */
@media (max-width: 768px) {
  /* Contenedor principal del modal */
  .reminder-container {
    /* Fija la altura total del modal, aprovechando el espacio desde abajo */
    height: 57.4vh;
    width: 100vw;

    /* Asegura que el contenido interno pueda scrollear */
    overflow: hidden; /* Esto ya estaba, pero lo confirmamos */
  }

  /* El cuerpo del modal (modal-body) es el que debe tener el scroll */
  .modal-body {
    /* Permite que el cuerpo ocupe el espacio restante y scrollee */
    flex-grow: 1;
    overflow-y: auto; /* Habilita el scroll vertical */

    /* Ajuste de padding para pantallas más pequeñas */
    padding: 0rem 1rem 0rem 1rem; /* Reducimos el padding horizontal */

    /* Aseguramos que los elementos dentro no se desborden horizontalmente */
    width: 100%;
    box-sizing: border-box;
    scrollbar-width: none;
  }

  /* El encabezado y el pie de página deben tener altura fija y no scrollear */
  .modal-header,
  .modal-footer {
    flex-shrink: 0; /* Impide que se achiquen */
  }

  .modal-header {
    padding: 2.04vh; /* Ajuste de padding para móvil */
    padding-top: 1.53vh;
  }
  .modal-header img {
    width: 5vw;
  }
  .classic-list {
    gap: 1.27vh;
  }

  .reminder-item {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    gap: 4.18vw;
    border-bottom: 0.016rem solid var(--text-primary);
    padding: 1.16vh 0px;
  }

  .reminder-prop {
    display: flex;
    justify-content: center;
    min-width: 19.72vw;
    flex-grow: 0;
  }

  .close-mobile {
    visibility: visible;
    height: 2.29vh;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  /* Ajustes específicos de elementos internos */
  .section {
    gap: 0.5rem; /* Reduce el espacio entre secciones */
  }

  /* Reducción de tamaños en inputs y textareas para un mejor fit */
  .input-asunto,
  .textarea-descripcion {
    padding: 1rem; /* Padding uniforme */
  }
  .textarea-descripcion {
    min-height: 15vh; /* Puede reducir el min-height del textarea */
  }

  /* Reubicamos el botón de cerrar para que no se pise */
  .modal-close {
    /* right: 1rem;
    top: 1rem; */
    visibility: collapse;
  }

  .btnsSection {
    margin-top: 0rem;
    padding: 1.25rem 0rem 0rem 0rem;
    box-shadow: none;
    /* Fuerza a los botones a ser más grandes y apilados si hay poco espacio */
    flex-direction: column;
    gap: 1.27vh;
  }

  .modal-footer {
    min-height: 2.55vh;
  }

  .actionBtn {
    width: 100%;
    padding: 0.96vh;
  }

  :deep(.inputMuniV2),
  .input-asunto {
    height: 4.59vh;
  }

  :deep(.datepicker-container .display-input) {
    height: 4.59vh;
  }

  /* animacion en mobile */
  @keyframes slideUp {
    from {
      transform: translateY(100%) translateX(-50%);
      opacity: 0;
    }
    to {
      transform: translateY(50) translateX(-50%);
      opacity: 1;
    }
  }
  @keyframes slideDown {
    from {
      transform: translateY(0) translateX(-50%);
      opacity: 1;
    }
    to {
      transform: translateY(100%) translateX(-50%);
      opacity: 0;
    }
  }

  :global(.modal-container[data-component="SeeReminders"].opening) {
    animation: slideUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
    opacity: 0;
  }

  :global(.modal-container[data-component="SeeReminders"].closing) {
    animation: slideDown 0.3s cubic-bezier(0.7, 0, 0.84, 0) forwards;
    pointer-events: none !important;
  }
}
</style>
